/**
 * ShiftSignOffModal - Guard Sign-Off Workflow
 * 
 * P0 BUSINESS RULE: Guards can ALWAYS sign off.
 * - Incomplete patrols show a warning, not a block
 * - Non-compliant shifts are flagged for supervisor review
 * - No supervisor override UI needed
 * 
 * SLED Compliance Sign-Off with:
 * - Patrol completion status (informational)
 * - Incident declaration
 * - Digital signature capture
 * - GPS location capture
 * - Handover notes
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { SignatureCanvas } from './ui/signature-canvas';
import { toast } from 'sonner';
import {
  CheckCircle2,
  AlertTriangle,
  PenLine,
  MapPin,
  Loader2,
  Shield,
  RefreshCw,
  FileText,
  AlertCircle,
  Route,
  ClipboardCheck,
  Info,
} from 'lucide-react';

// Device ID generator
const generateDeviceId = () => {
  let deviceId = localStorage.getItem('bsms_device_id');
  if (!deviceId) {
    deviceId = `web-${navigator.userAgent.slice(0, 20).replace(/\W/g, '')}-${Date.now().toString(36)}`;
    localStorage.setItem('bsms_device_id', deviceId);
  }
  return deviceId;
};

export function ShiftSignOffModal({ 
  shift, 
  isOpen, 
  onClose, 
  api, 
  onSignOffComplete 
}) {
  const [currentStep, setCurrentStep] = useState('check'); // check, notes, signature
  const [loading, setLoading] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(true);
  
  // Sign-off data
  const [signatureData, setSignatureData] = useState(null);
  const [notes, setNotes] = useState('');
  const [handoverNotes, setHandoverNotes] = useState('');
  const [incidentsReported, setIncidentsReported] = useState(false);
  const [incidentSummary, setIncidentSummary] = useState('');
  
  // GPS state
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  
  // Check eligibility on mount
  useEffect(() => {
    if (isOpen && shift?.id) {
      checkEligibility();
      requestGPS();
    }
  }, [isOpen, shift?.id]);
  
  // Check sign-off eligibility
  const checkEligibility = async () => {
    setEligibilityLoading(true);
    try {
      const response = await api.get(`/shifts/${shift.id}/sign-off-eligibility`);
      setEligibility(response.data);
      
      // P0: Always proceed to notes step since sign-off is always allowed
      // (unless shift hasn't started yet)
      if (response.data.eligible) {
        setCurrentStep('notes');
      }
    } catch (error) {
      toast.error('Failed to check eligibility');
      setEligibility({
        eligible: false,
        reason: 'Failed to check eligibility',
        blocking_issues: ['error'],
      });
    } finally {
      setEligibilityLoading(false);
    }
  };
  
  // Request GPS location
  const requestGPS = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsError('Geolocation not supported');
      return;
    }
    
    setGpsLoading(true);
    setGpsError(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        setGpsLoading(false);
      },
      (error) => {
        setGpsError(error.message);
        setGpsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  }, []);
  
  // Handle sign-off (P0: Always allowed)
  const handleSignOff = async () => {
    console.log('[SIGN-OFF] handleSignOff called');
    console.log('[SIGN-OFF] signatureData exists:', !!signatureData);
    console.log('[SIGN-OFF] gpsLocation:', gpsLocation);
    console.log('[SIGN-OFF] shift:', shift);
    
    if (!signatureData) {
      console.log('[SIGN-OFF] BLOCKED: No signature');
      toast.error('Please provide your signature');
      return;
    }
    
    // P0 FIX: GPS is preferred but NOT required - allow sign-off with gps_verified flag
    const hasGPS = !!gpsLocation;
    
    setLoading(true);
    
    const payload = {
      signature_data: signatureData,
      latitude: gpsLocation?.latitude || null,
      longitude: gpsLocation?.longitude || null,
      gps_verified: hasGPS,  // P0: Track if GPS was captured
      device_id: generateDeviceId(),
      notes,
      handover_notes: handoverNotes,
      incidents_reported: incidentsReported,
      incident_summary: incidentsReported ? incidentSummary : null,
    };
    
    console.log('[SIGN-OFF] Payload:', payload);
    console.log('[SIGN-OFF] API URL:', `/shifts/${shift.id}/sign-off`);
    
    // P0: Retry logic for sign-off - max 3 attempts
    let lastError = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        console.log(`[SIGN-OFF] Attempt ${attempt}/3`);
        const response = await api.post(`/shifts/${shift.id}/sign-off`, payload, {
          timeout: 30000,  // 30 second timeout for sign-off request
        });
        
        console.log('[SIGN-OFF] SUCCESS:', response.data);
        
        // P0: Show appropriate success message based on compliance
        const hasWarning = eligibility?.has_warning || !eligibility?.patrol_status?.is_complete;
        if (hasWarning) {
          toast.success('Shift signed off - flagged for review due to incomplete patrols');
        } else if (!hasGPS) {
          toast.success('Shift signed off - GPS unavailable, flagged for review');
        } else {
          toast.success('Shift signed off successfully!');
        }
        
        onSignOffComplete?.(response.data);
        onClose();
        return;  // Success - exit function
      } catch (error) {
        console.error(`[SIGN-OFF] Attempt ${attempt} failed:`, error);
        console.error(`[SIGN-OFF] Error response:`, error.response?.data);
        console.error(`[SIGN-OFF] Error status:`, error.response?.status);
        lastError = error;
        
        // If it's a 401, the auth interceptor will handle refresh
        // If it's a timeout or network error, retry
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          toast.warning(`Connection slow, retrying... (${attempt}/3)`);
          await new Promise(r => setTimeout(r, 1000));  // Wait 1 second before retry
          continue;
        }
        
        // For other errors, break immediately
        break;
      }
    }
    
    // All retries failed
    console.error('[SIGN-OFF] All retries failed:', lastError);
    setLoading(false);
    const detail = lastError?.response?.data?.detail;
    const errorMessage = typeof detail === 'string' ? detail : 
                        lastError?.message?.includes('timeout') ? 'Request timed out - please check your connection and try again' :
                        'Sign-off failed. Please try again.';
    toast.error(errorMessage);
  };
  
  // Render patrol status
  const renderPatrolStatus = () => {
    const status = eligibility?.patrol_status;
    if (!status) return null;
    
    const isComplete = status.is_complete;
    const hasWarning = eligibility?.has_warning;
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 bg-[#09090b] rounded-lg border border-[#3f3f46]">
          <div className="flex items-center gap-2">
            <Route className="w-5 h-5 text-blue-400" />
            <span className="text-white">Patrol Completion</span>
          </div>
          <Badge className={isComplete ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
            {status.completion_percentage}%
          </Badge>
        </div>
        
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="p-2 bg-[#09090b] rounded text-center">
            <p className="text-2xl font-bold text-white">{status.completed_checkpoints}</p>
            <p className="text-xs text-zinc-500">Checkpoints Done</p>
          </div>
          <div className="p-2 bg-[#09090b] rounded text-center">
            <p className="text-2xl font-bold text-white">{status.total_checkpoints}</p>
            <p className="text-xs text-zinc-500">Total Checkpoints</p>
          </div>
        </div>
        
        {/* P0: Show warning banner for incomplete patrols */}
        {hasWarning && (
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-300">Incomplete Patrols</p>
                <p className="text-xs text-amber-200/70 mt-1">
                  You can still sign off, but this shift will be flagged for supervisor review.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {status.incomplete_patrols?.length > 0 && (
          <div className="p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
            <p className="text-sm font-medium text-zinc-300 mb-2">Patrol Details:</p>
            <ul className="space-y-1 text-sm text-zinc-400">
              {status.incomplete_patrols.slice(0, 5).map((patrol, idx) => (
                <li key={idx} className="flex items-center justify-between">
                  <span>{patrol.route_name}</span>
                  <span className="text-xs">{patrol.completed}/{patrol.total}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };
  
  // Render step content
  const renderContent = () => {
    // Loading state
    if (eligibilityLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-10 h-10 text-blue-400 animate-spin mb-4" />
          <p className="text-zinc-400">Checking sign-off status...</p>
        </div>
      );
    }
    
    // Check step - show patrol status
    if (currentStep === 'check') {
      // P0: This step is now only shown if shift hasn't started yet
      const notStarted = eligibility?.blocking_issues?.includes('shift_not_started');
      
      return (
        <div className="space-y-6">
          <div className="text-center">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
              notStarted ? 'bg-red-500/20' : 'bg-emerald-500/20'
            }`}>
              {notStarted ? (
                <AlertCircle className="w-8 h-8 text-red-400" />
              ) : (
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              )}
            </div>
            <h3 className="text-xl font-bold text-white mt-4">
              {notStarted ? 'Cannot Sign Off' : 'Ready to Sign Off'}
            </h3>
            <p className="text-zinc-400 mt-1">{eligibility?.reason}</p>
          </div>
          
          {renderPatrolStatus()}
          
          {!notStarted && (
            <Button
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-600"
              onClick={() => setCurrentStep('notes')}
              data-testid="continue-signoff-btn"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              Continue to Sign-Off
            </Button>
          )}
          
          <Button
            variant="outline"
            className="w-full border-[#3f3f46]"
            onClick={checkEligibility}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Status
          </Button>
        </div>
      );
    }
    
    // Notes step
    if (currentStep === 'notes') {
      const hasWarning = eligibility?.has_warning;
      
      return (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-blue-500/20 flex items-center justify-center">
              <FileText className="w-7 h-7 text-blue-400" />
            </div>
            <h3 className="text-lg font-bold text-white mt-3">End of Shift Notes</h3>
          </div>
          
          {/* P0: Warning banner for incomplete patrols */}
          {hasWarning && !eligibility?.is_interrupted && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-300">
                  Patrols incomplete ({eligibility?.patrol_status?.completion_percentage || 0}%). 
                  This shift will be flagged for supervisor review.
                </p>
              </div>
            </div>
          )}
          
          {/* PHASE 2: Interruption notice */}
          {eligibility?.is_interrupted && eligibility?.interruption && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-300">
                  <p className="font-medium">Patrol Interrupted</p>
                  <p className="text-amber-300/80 mt-1">
                    Reason: {eligibility.interruption.reason}
                  </p>
                  {eligibility.interruption.interrupted_at && (
                    <p className="text-amber-300/60 text-xs mt-1">
                      At: {new Date(eligibility.interruption.interrupted_at).toLocaleTimeString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Incident Declaration */}
          <div className="p-4 bg-[#09090b] rounded-lg border border-[#3f3f46] space-y-3">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="incidents"
                checked={incidentsReported}
                onCheckedChange={setIncidentsReported}
                className="border-zinc-500"
                data-testid="incidents-checkbox"
              />
              <Label htmlFor="incidents" className="text-white cursor-pointer">
                Incidents occurred during this shift
              </Label>
            </div>
            
            {incidentsReported && (
              <div className="space-y-2 pt-2">
                <Label className="text-zinc-400">Incident Summary</Label>
                <Textarea
                  value={incidentSummary}
                  onChange={(e) => setIncidentSummary(e.target.value)}
                  placeholder="Describe the incident(s)..."
                  className="bg-[#18181b] border-[#3f3f46] min-h-[80px]"
                  data-testid="incident-summary-input"
                />
              </div>
            )}
            
            {!incidentsReported && (
              <p className="text-xs text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                No incidents will be recorded for this shift
              </p>
            )}
          </div>
          
          {/* End of Shift Notes */}
          <div className="space-y-2">
            <Label className="text-zinc-400">End of Shift Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about the shift..."
              className="bg-[#09090b] border-[#3f3f46] min-h-[80px]"
              data-testid="shift-notes-input"
            />
          </div>
          
          {/* Handover Notes */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Handover Notes for Next Officer (Optional)</Label>
            <Textarea
              value={handoverNotes}
              onChange={(e) => setHandoverNotes(e.target.value)}
              placeholder="Information for the next officer..."
              className="bg-[#09090b] border-[#3f3f46] min-h-[60px]"
              data-testid="handover-notes-input"
            />
          </div>
          
          <Button
            className="w-full h-12 bg-blue-500 hover:bg-blue-600"
            onClick={() => setCurrentStep('signature')}
            data-testid="continue-to-signature-btn"
          >
            <PenLine className="w-5 h-5 mr-2" />
            Continue to Signature
          </Button>
        </div>
      );
    }
    
    // Signature step
    if (currentStep === 'signature') {
      const hasWarning = eligibility?.has_warning;
      
      return (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center">
              <PenLine className="w-7 h-7 text-emerald-400" />
            </div>
            <h3 className="text-lg font-bold text-white mt-3">Sign Off Confirmation</h3>
            <p className="text-sm text-zinc-400 mt-1">
              Your signature confirms completion of shift duties
            </p>
          </div>
          
          {/* P0: Warning reminder for incomplete patrols */}
          {hasWarning && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <p className="text-sm text-amber-300">
                  This shift will be flagged for review due to incomplete patrols.
                </p>
              </div>
            </div>
          )}
          
          {/* GPS Status - P0: Show that sign-off is allowed even without GPS */}
          <div className="p-3 bg-[#09090b] rounded-lg border border-[#3f3f46]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className={`w-4 h-4 ${gpsLocation ? 'text-emerald-400' : gpsError ? 'text-amber-400' : 'text-zinc-400'}`} />
                <span className="text-sm text-zinc-300">
                  {gpsLoading ? 'Acquiring GPS...' : 
                   gpsLocation ? 'Location captured' : 
                   gpsError ? 'GPS unavailable - will flag for review' : 
                   'Acquiring location...'}
                </span>
              </div>
              {!gpsLocation && !gpsLoading && (
                <Button size="sm" variant="ghost" onClick={requestGPS} disabled={gpsLoading}>
                  <RefreshCw className={`w-4 h-4 ${gpsLoading ? 'animate-spin' : ''}`} />
                </Button>
              )}
            </div>
            {gpsLocation ? (
              <p className="text-xs text-zinc-500 mt-1 font-mono">
                {gpsLocation.latitude.toFixed(6)}, {gpsLocation.longitude.toFixed(6)}
              </p>
            ) : gpsError && (
              <p className="text-xs text-amber-400 mt-1">
                You can still sign off - shift will be flagged for operational review
              </p>
            )}
          </div>
          
          {/* Signature Canvas */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Sign-Off Signature</Label>
            <SignatureCanvas
              onSignatureChange={setSignatureData}
              width={340}
              height={150}
              className="bg-white rounded-lg"
            />
          </div>
          
          {/* Summary */}
          <div className="p-3 bg-[#09090b] rounded-lg border border-[#3f3f46] space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-500">Incidents:</span>
              <span className={incidentsReported ? 'text-amber-400' : 'text-emerald-400'}>
                {incidentsReported ? 'Yes - Reported' : 'None'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Notes:</span>
              <span className="text-zinc-300">{notes ? 'Added' : 'None'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Handover:</span>
              <span className="text-zinc-300">{handoverNotes ? 'Added' : 'None'}</span>
            </div>
            {hasWarning && (
              <div className="flex justify-between border-t border-zinc-700 pt-2 mt-2">
                <span className="text-zinc-500">Review Status:</span>
                <span className="text-amber-400">Flagged for Review</span>
              </div>
            )}
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 border-[#3f3f46]"
              onClick={() => setCurrentStep('notes')}
            >
              Back
            </Button>
            <Button
              className={`flex-1 h-12 ${hasWarning || !gpsLocation ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-500 hover:bg-emerald-600'}`}
              onClick={handleSignOff}
              disabled={loading || !signatureData}
              data-testid="complete-signoff-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <ClipboardCheck className="w-5 h-5 mr-2" />
              )}
              {hasWarning || !gpsLocation ? 'Sign Off (Flag for Review)' : 'Complete Sign-Off'}
            </Button>
          </div>
        </div>
      );
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5 text-emerald-400" />
            Sign Off Shift
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {shift?.site_name} • {shift?.site_code}
          </DialogDescription>
        </DialogHeader>
        
        {/* Step Indicators */}
        <div className="flex items-center justify-center gap-2 py-2">
          {['check', 'notes', 'signature'].map((step, idx) => (
            <div key={step} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                currentStep === step ? 'bg-blue-500 text-white' :
                ['check', 'notes', 'signature'].indexOf(currentStep) > idx ? 'bg-emerald-500/20 text-emerald-400' :
                'bg-[#3f3f46] text-zinc-500'
              }`}>
                {idx + 1}
              </div>
              {idx < 2 && <div className="w-6 h-0.5 bg-[#3f3f46]" />}
            </div>
          ))}
        </div>
        
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}

export default ShiftSignOffModal;
